import React, { createContext, useCallback, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { isValidModelFilterData } from '../../utils/modelFilter';

/**
 * @typedef {Object} FilterItem
 * @property {string} id - Unique identifier for the filter item
 * @property {string} text - Display text for the filter item
 */

/**
 * @typedef {Object} ModelFilterData
 * @property {FilterItem[]|null} type - Selected type filters
 * @property {FilterItem[]|null} brand - Selected brand filters
 * @property {FilterItem[]|null} model - Selected model filters
 */

const ModelFilterContext = createContext();

/**
 * Provider component for model filter state
 */
export const ModelFilterProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies();
  const modelFilterCookie = isValidModelFilterData(cookies?.modelFilterData)
    ? cookies.modelFilterData
    : null;

  /** @type {[ModelFilterData | null, Function]} */
  const [data, _setData] = useState(modelFilterCookie);

  const setData = useCallback(
    props => {
      const newData = typeof props === 'function' ? props(data) : props;
      _setData(newData);

      // Remove category id:s from the data id:s
      // e.g. { type: [{ id: 'Moped_Klass_2:modellfilter:2810', text: '...' }] } should remove '2810'
      for (const key of Object.keys(newData)) {
        if (Array.isArray(newData[key])) {
          for (const item of newData[key]) {
            if (typeof item.id !== 'string') {
              throw new Error(`Invalid filter item id: ${item.id}`);
            }
            item.id = item.id.slice(0, item.id.lastIndexOf(':') + 1);
          }
        }
      }

      setCookie('modelFilterData', newData, {
        path: '/',
        expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
      });
    },
    [data, _setData, setCookie]
  );

  const getItems = useCallback(
    (_data = data) => {
      const type = _data?.type?.[0]?.text;
      const brand = _data?.brand?.[0]?.text;
      const model = _data?.model?.[0]?.text;

      const values = [];
      if (type) {
        values.push(type);
        if (brand) {
          values.push(brand);
          if (model) {
            values.push(model);
          }
        }
      }
      return values;
    },
    [data]
  );

  return (
    <ModelFilterContext.Provider value={{ data, setData, getItems }}>
      {children}
    </ModelFilterContext.Provider>
  );
};

/**
 * Hook to access and modify model filter state
 * @returns {{ data: ModelFilterData | null, setData: Function, getItems: (data?: ModelFilterData | null) => string[]}} Context containing data and setData function
 */
export const useModelFilter = () => useContext(ModelFilterContext);
